import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { StepType } from '../../models/step-type.model';
import { User } from '../../models/user.model';
import { DataService } from '../../services/data.service';
import { ValidateService } from '../../services/validate.service';
import { StepComponent } from '../step/step.component';

@Component({
  selector: 'app-yp-step',
  templateUrl: './your-profile-step.component.html',
  styleUrls: ['./your-profile-step.component.scss'],
  providers: [DataService, ValidateService]
})
export class YourProfileStepComponent extends StepComponent implements OnInit {
  public yourProfileForm: FormGroup;
  public countries: string[];
  @Input() public user: User;
  constructor(
    translate: TranslateService,
    private formBuilder: FormBuilder,
    private dataService: DataService,
    private validateService: ValidateService) {
    super(translate);
  }

  ngOnInit() {
    this.setTitle('MyProfile_YP_YourProfile');
    this.yourProfileForm = this.initYourProfileForm();
    this.yourProfileForm.valueChanges.subscribe(val => {
      this.modelChange();
    });

    this.dataService.getCountries().subscribe(data => {
      this.setCountries(data);
    });
  }

  public modelChange() {
    this.setupAccountModel();
    this.stepModelChanged.emit({
      stepType: StepType.YourProfile,
      validState: this.yourProfileForm.valid
    });
  }

  public get selectedCountry(): string {
    const selectedCountry = this.stepAccount.country ? this.stepAccount.country : 'United States';
    return this.countries[this.countries.indexOf(selectedCountry)];
  }

  get firstName() {
    return this.yourProfileForm.get('firstName');
  }
  get lastName() {
    return this.yourProfileForm.get('lastName');
  }
  get workPhone() {
    return this.yourProfileForm.get('workPhone');
  }
  get mobilePhone() {
    return this.yourProfileForm.get('mobilePhone');
  }
  get companyName() {
    return this.yourProfileForm.get('companyName');
  }
  get department() {
    return this.yourProfileForm.get('department');
  }
  get title() {
    return this.yourProfileForm.get('title');
  }
  get street1() {
    return this.yourProfileForm.get('street1');
  }
  get street2() {
    return this.yourProfileForm.get('street2');
  }
  get state() {
    return this.yourProfileForm.get('state');
  }
  get city() {
    return this.yourProfileForm.get('city');
  }
  get zip() {
    return this.yourProfileForm.get('zip');
  }

  private initYourProfileForm(): FormGroup {

    return this.formBuilder.group({
      firstName: [
        this.stepAccount.firstName = this.user.firstName,
        { validators: [Validators.required, this.validateService.prohibitAmpersandValidator()] }
      ],
      lastName: [
        this.stepAccount.lastName = this.user.lastName,
        { validators: [Validators.required, this.validateService.prohibitAmpersandValidator()] }
      ],
      workPhone: [
        this.stepAccount.workPhone = this.user.workPhone,
        { validators: this.isOptional() ?  [this.validateService.prohibitedSymbolsValidator()] : [Validators.required, this.validateService.prohibitedSymbolsValidator()] }
      ],
      mobilePhone: [this.stepAccount.mobilePhone, { validators: this.validateService.prohibitedSymbolsValidator() }],
      companyName: [
        this.stepAccount.companyName = this.user.companyName,
        { validators: this.isOptional() ? [this.validateService.prohibitApostropheValidator()] : [Validators.required, this.validateService.prohibitApostropheValidator()] }
      ],
      department: [
        this.stepAccount.department = this.user.department,
        { validators: this.isOptional() ?  [this.validateService.prohibitedSymbolsValidator()] : [Validators.required, this.validateService.prohibitedSymbolsValidator()] }
      ],
      title: [
        this.stepAccount.title = this.user.title,
        { validators: this.isOptional() ? [this.validateService.prohibitApostropheValidator()] : [Validators.required, this.validateService.prohibitApostropheValidator()] }
      ],
      street1: [
        this.stepAccount.street1 = this.user.street1,
        { validators: this.isOptional() ?  [this.validateService.prohibitedSymbolsValidator()] : [Validators.required, this.validateService.prohibitedSymbolsValidator()] }
      ],
      street2: [
        this.stepAccount.street2 = this.user.street2, 
        { validators: this.validateService.prohibitedSymbolsValidator() }
      ],
      country: [
        this.stepAccount.country = this.user.country,
         { validators: this.isOptional() ? [] : [Validators.required] }
      ],
      state: [
        this.stepAccount.state = this.user.state,
        { validators: this.isOptional() ?  [this.validateService.prohibitedSymbolsValidator()] : [Validators.required, this.validateService.prohibitedSymbolsValidator()] }
      ],
      city: [
        this.stepAccount.city = this.user.city,
        { validators: this.isOptional() ?  [this.validateService.prohibitedSymbolsValidator()] : [Validators.required, this.validateService.prohibitedSymbolsValidator()] }
      ],
      zip: [
        this.stepAccount.zip = this.user.zip,
        { validators: this.isOptional() ?  [this.validateService.prohibitedSymbolsValidator()] : [Validators.required, this.validateService.prohibitedSymbolsValidator()] }
      ]
    });
  }

  private setupAccountModel() {
    Object.keys(this.yourProfileForm.controls).forEach(key => {
      this.stepAccount[key] = this.yourProfileForm.get(key).value;
    });
  }

  private setCountries(countries: string[]) {
    this.countries = countries;
    this.yourProfileForm.get('country').setValue(this.selectedCountry);
  }

  public isOptional(){
    return !this.stepAccount.outOptions.contactWithAdditionalServices && !this.stepAccount.outOptions.contactWithMarketingMaterials;
  }
}
